/**
 * Order price/size rules (#675) and outcome trading rules (#676, pure logic).
 *
 * Hyperliquid tick/lot rules (docs: for-developers/api/tick-and-lot-size):
 *   - size: at most `szDecimals` decimals;
 *   - price: at most 5 significant figures AND at most MAX_DECIMALS - szDecimals decimals
 *     (MAX_DECIMALS = 8 spot, 6 perp); integer prices are always allowed.
 *
 * HIP-4 outcomes (docs/research/OUTCOME_IDS.md section 4 + live checks 2026-10-16):
 *   - no szDecimals is published (outcomeMeta has none, outcome tokens are absent from spotMeta.tokens) but every
 *     observed size is a whole share, so outcomes behave as szDecimals = 0;
 *   - the generic spot formula would then allow 8 decimals, yet no book level / order on either net ever had more
 *     than 5 decimals, so outcome prices are capped at 5 decimals (OUTCOME_PX_DECIMALS) and must lie strictly in
 *     (0, 1): 0.00001 ... 0.99999. In (0, 1), 5 decimals implies <= 5 significant figures, so 0.00001 passes both.
 *   - markPx / midPx from asset ctxs can carry 6 decimals (they are derived, not order prices): always run them
 *     through roundPrice before using them as a limit price.
 *   - min order value (#749): 1 USDC, a HARD block. Hyperliquid rejects a smaller order with "Order must have minimum
 *     value of 1 USDC. asset=<id>" (live testnet, docs/testing/745 E9: ALO buy 1 @ 0.01). Measured as limitPx * sz,
 *     with one exemption, from 7,740 accepted testnet outcome orders (docs/research/samples/s749-min-value-testnet.txt,
 *     tool docs/research/tools/min_value_probe.py, read-only /info):
 *       - buys: 0 of 3,059 ALO / 738 IOC and 2 of 1,694 GTC buys were below 1 USDC at limitPx * origSz;
 *       - sells below 1 USDC were accepted only when they sold the WHOLE holding of that side (every such limit sell
 *         whose position could be rebuilt from userFills sold exactly the shares held), so selling everything is
 *         never blocked;
 *     a value of exactly 1 USDC is allowed. Market orders (#746) are not measured at their bound price: see
 *     ticket/order.ts checkMarketTicket (they only warn, at the estimated fill).
 *
 * All arithmetic is exact (./decimal), so 1 - 0.58504 is 0.41496 and never 0.41495999999999995.
 */
import { fromCoin, OUTCOME_PX_DECIMALS, type Side } from '../hl/outcomes';
import { floatToWire } from '../hl/signing';
import {
  cmp,
  decimalsOf,
  decToNumber,
  decToString,
  mul,
  ONE,
  parseDec,
  roundDec,
  sigFigsOf,
  sub,
  ZERO,
  type Dec,
  type RoundMode,
} from './decimal';
import { t } from '../i18n/i18n';
import { localizeNumber, usd } from '../i18n/format';

export type { RoundMode } from './decimal';

export const PERP_MAX_DECIMALS = 6;
export const SPOT_MAX_DECIMALS = 8;
export const MAX_SIG_FIGS = 5;
/** Hyperliquid's minimum order value for outcomes, in USDC (#749: "Order must have minimum value of 1 USDC"). */
export const MIN_ORDER_VALUE_USD = 1;

export interface MarketRules {
  /** Size decimals (0 = whole units). Outcomes: 0. */
  szDecimals: number;
  /** Spot-like (MAX_DECIMALS 8) vs perp (6). Outcomes are spot-like. */
  isSpotLike: boolean;
  /** HIP-4 outcome market: price in (0, 1) with <= 5 decimals, whole-share sizes. */
  isOutcome?: boolean;
}

export const OUTCOME_RULES: Readonly<MarketRules> = Object.freeze({ szDecimals: 0, isSpotLike: true, isOutcome: true });

export type Validation = { ok: true } | { ok: false; reason: string };

type Num = number | string;

const okResult: Validation = { ok: true };
const fail = (reason: string): Validation => ({ ok: false, reason });

function checkRules(ctx: MarketRules): void {
  if (!Number.isInteger(ctx.szDecimals) || ctx.szDecimals < 0) throw new Error(`invalid szDecimals: ${ctx.szDecimals}`);
}

/** Max price decimals for a market: outcomes 5; otherwise MAX_DECIMALS - szDecimals (never below 0). */
export function maxPriceDecimals(ctx: MarketRules): number {
  checkRules(ctx);
  if (ctx.isOutcome) return OUTCOME_PX_DECIMALS;
  return Math.max(0, (ctx.isSpotLike ? SPOT_MAX_DECIMALS : PERP_MAX_DECIMALS) - ctx.szDecimals);
}

/** Decimals that keep `d` at <= 5 significant figures (0 once the integer part has >= 5 digits). */
function sigFigDecimals(d: Dec): number {
  const abs = d.n < 0n ? { n: -d.n, s: d.s } : d;
  if (cmp(abs, ONE) >= 0) {
    const intDigits = (abs.n / 10n ** BigInt(abs.s)).toString().length;
    return Math.max(0, MAX_SIG_FIGS - intDigits);
  }
  // 0.000ddd: leading zeros after the point + 5.
  const frac = abs.n.toString().padStart(abs.s, '0');
  const leadingZeros = frac.length - frac.replace(/^0+/, '').length;
  return leadingZeros + MAX_SIG_FIGS;
}

function parseOrThrow(v: Num, what: string): Dec {
  const d = parseDec(v);
  if (!d) throw new Error(`invalid ${what}: ${String(v)}`);
  return d;
}

/**
 * Round a price to the market's tick: one rounding step to min(sig-fig decimals, max decimals), so the result has
 * <= 5 significant figures (or is an integer) and <= maxPriceDecimals decimals. Returns a wire-shaped string.
 * Does NOT clamp: an outcome price that rounds to 0 or 1 is returned as-is and rejected by validatePrice.
 * Throws on a non-numeric input.
 */
export function roundPrice(px: Num, ctx: MarketRules, mode: RoundMode = 'nearest'): string {
  const d = parseOrThrow(px, 'price');
  const maxDec = maxPriceDecimals(ctx);
  const decimals = ctx.isOutcome ? maxDec : Math.min(maxDec, sigFigDecimals(d));
  return decToString(roundDec(d, decimals, mode));
}

/** Validate a price as typed by the user / about to be sent. Reasons are human-readable. */
export function validatePrice(px: Num, ctx: MarketRules): Validation {
  const d = parseDec(px);
  if (!d) return fail(t('rules.price_invalid'));
  if (cmp(d, ZERO) <= 0) return fail(t('rules.price_positive'));
  const maxDec = maxPriceDecimals(ctx);
  if (ctx.isOutcome) {
    if (cmp(d, ONE) >= 0) return fail(t('rules.price_below_one'));
    if (decimalsOf(d) > maxDec) {
      return fail(t('rules.price_decimals_outcome', { n: String(maxDec), tick: localizeNumber(decToString({ n: 1n, s: maxDec })) }));
    }
    return okResult;
  }
  const isInteger = decimalsOf(d) === 0;
  if (isInteger) return okResult;
  if (sigFigsOf(d) > MAX_SIG_FIGS) return fail(t('rules.price_sig_figs', { n: String(MAX_SIG_FIGS) }));
  if (decimalsOf(d) > maxDec) return fail(t('rules.price_decimals', { n: String(maxDec) }));
  return okResult;
}

/** Round a size to szDecimals. Default 'down' (never send more than the user asked for / holds). */
export function roundSize(sz: Num, ctx: MarketRules, mode: RoundMode = 'down'): string {
  checkRules(ctx);
  return decToString(roundDec(parseOrThrow(sz, 'size'), ctx.szDecimals, mode));
}

export function validateSize(sz: Num, ctx: MarketRules): Validation {
  checkRules(ctx);
  const d = parseDec(sz);
  if (!d) return fail(t('rules.size_invalid'));
  if (cmp(d, ZERO) <= 0) return fail(ctx.isOutcome ? t('rules.size_min_share') : t('rules.size_positive'));
  if (decimalsOf(d) > ctx.szDecimals) {
    if (ctx.isOutcome || ctx.szDecimals === 0) return fail(t('rules.size_whole'));
    return fail(t('rules.size_decimals', { n: String(ctx.szDecimals) }));
  }
  return okResult;
}

/** Exact notional px * sz as a decimal string. */
export function notionalExact(px: Num, sz: Num): string {
  return decToString(mul(parseOrThrow(px, 'price'), parseOrThrow(sz, 'size')));
}

/** Notional px * sz (USDC for outcomes), computed exactly then converted to a number. */
export function notional(px: Num, sz: Num): number {
  return Number(notionalExact(px, sz));
}

/**
 * Blocking error when the order value px * sz is below `minUsd` (default: Hyperliquid's 1 USDC; exactly 1 is fine).
 * The value is printed exactly (0.33333 * 3 is "$0.99999", never a rounded "$1"). Returns null when the order is big
 * enough or the inputs are not positive numbers (other checks report those).
 */
export function minOrderValueError(px: Num, sz: Num, minUsd: number = MIN_ORDER_VALUE_USD): string | null {
  const p = parseDec(px);
  const s = parseDec(sz);
  const min = parseDec(minUsd);
  if (!p || !s || !min || cmp(min, ZERO) <= 0) return null;
  const n = mul(p, s);
  if (cmp(n, ZERO) <= 0 || cmp(n, min) >= 0) return null;
  return t('rules.min_value', { value: usd(decToString(n)), min: localizeNumber(decToString(min)) });
}

/**
 * Does `sz` sell the whole holding of that side? Hyperliquid accepts such a sell below the minimum order value (#749),
 * so a small leftover position can always be closed.
 */
export function sellsWholeHolding(sz: Num, held: Num): boolean {
  const s = parseDec(sz);
  const h = sellable(held);
  return !!s && cmp(h, ZERO) > 0 && cmp(s, h) === 0;
}

// ---------------------------------------------------------------------------------------------------------------
// Mirrored book: Yes and No share one book. Buying side 1 at q == selling side 0 at 1 - q.

/** Price of `side` given the side-0 (Yes) price: side 0 -> p, side 1 -> 1 - p. Exact; returns a wire-shaped string. */
export function toSidePrice(yesPx: Num, side: Side): string {
  const d = parseOrThrow(yesPx, 'price');
  if (side === 0) return decToString(d);
  if (side === 1) return decToString(sub(ONE, d));
  throw new Error(`invalid outcome side: ${String(side)}`);
}

/**
 * Implied probability, in percent, of a side priced at `px` (#777): a share that pays $1 if its side wins and costs
 * `px` is priced like a `px * 100`% chance (0.63 -> "63", 0.58504 -> "58.504"). Exact. Null for junk or a price
 * outside [0, 1]. Market prices also carry the spread, so this is the market's estimate, not a guarantee.
 */
export function impliedProbabilityPct(px: Num): string | null {
  const d = parseDec(px);
  if (!d || cmp(d, ZERO) < 0 || cmp(d, ONE) > 0) return null;
  return decToString(mul(d, { n: 100n, s: 0 }));
}

// ---------------------------------------------------------------------------------------------------------------
// Outcome trading rules (spec 2.5): no leverage; BUY Yes/No always allowed; SELL side X only up to the X shares held.

/** Sellable shares per side (balance `total - hold`: `hold` = shares already locked by resting sells). */
export interface OutcomeHoldings {
  holdYes: Num;
  holdNo: Num;
}

export interface AllowedActions {
  buyYes: true;
  buyNo: true;
  sellYes: boolean;
  sellNo: boolean;
  /** Whole shares that can still be sold on each side. */
  maxSellYes: number;
  maxSellNo: number;
}

/** Sellable whole shares from a holding amount (fractions and negatives floor to 0). */
function sellable(v: Num): Dec {
  const d = parseDec(v);
  if (!d || cmp(d, ZERO) <= 0) return ZERO;
  return roundDec(d, 0, 'down');
}

export function allowedActions(h: OutcomeHoldings): AllowedActions {
  const y = sellable(h.holdYes);
  const n = sellable(h.holdNo);
  return {
    buyYes: true,
    buyNo: true,
    sellYes: cmp(y, ZERO) > 0,
    sellNo: cmp(n, ZERO) > 0,
    maxSellYes: decToNumber(y),
    maxSellNo: decToNumber(n),
  };
}

/** A spot balance row as in spotClearinghouseState.balances / WS spotState (strings from the API). */
export interface BalanceRow {
  coin: string;
  total: string;
  hold: string;
  entryNtl?: string;
}

export interface SideBalance {
  total: string;
  hold: string;
  /** total - hold (what can still be sold / spent). */
  available: string;
}

export interface OutcomeBalances {
  yes: SideBalance;
  no: SideBalance;
}

const EMPTY_SIDE: SideBalance = Object.freeze({ total: '0', hold: '0', available: '0' });

function sideBalance(row: BalanceRow): SideBalance {
  const t = parseDec(row.total) ?? ZERO;
  const h = parseDec(row.hold) ?? ZERO;
  const a = sub(t, h);
  return { total: decToString(t), hold: decToString(h), available: decToString(cmp(a, ZERO) > 0 ? a : ZERO) };
}

/** Pick the "+<enc>" rows of one outcome out of a balances list (ids parsed by outcomes.fromCoin only). */
export function outcomeBalances(balances: readonly BalanceRow[], outcome: number): OutcomeBalances {
  let yes: SideBalance = EMPTY_SIDE;
  let no: SideBalance = EMPTY_SIDE;
  for (const row of balances) {
    if (!row.coin.startsWith('+')) continue;
    const ref = fromCoin(row.coin);
    if (!ref || ref.outcome !== outcome) continue;
    if (ref.side === 0) yes = sideBalance(row);
    else no = sideBalance(row);
  }
  return { yes, no };
}

/** Holdings for allowedActions / validateOrderIntent from a balances list. */
export function outcomeHoldings(balances: readonly BalanceRow[], outcome: number): OutcomeHoldings {
  const b = outcomeBalances(balances, outcome);
  return { holdYes: b.yes.available, holdNo: b.no.available };
}

/** Available USDC (`total - hold`) from a balances list; "0" if there is no USDC row. */
export function availableUsdc(balances: readonly BalanceRow[]): string {
  const row = balances.find((b) => b.coin === 'USDC');
  return row ? sideBalance(row).available : '0';
}

export interface OrderIntent {
  side: Side;
  isBuy: boolean;
  sz: Num;
  /** Limit price of `side` (omit to skip price/cost checks, e.g. while the user is still typing). */
  px?: Num;
  holdings: OutcomeHoldings;
  /** Available USDC (`total - hold`). Omit to skip the buying-power check. */
  usdcAvailable?: Num;
  /** Defaults to true for sells, false for buys. */
  reduceOnly?: boolean;
  /** Labels for messages, default ["Yes", "No"] (use outcomes.sideName for sports etc.). */
  sideLabels?: readonly [string, string];
  /** Minimum order value in USDC, default Hyperliquid's 1 (MIN_ORDER_VALUE_USD). */
  minOrderValueUsd?: number;
  /**
   * false skips the min-order-value block. Market orders (#746) send `px` = the price bound, which says nothing about
   * the order's value, so checkMarketTicket checks the estimated fill itself (as a warning). Default true.
   */
  checkMinValue?: boolean;
}

export type IntentResult =
  | { ok: true; reduceOnly: boolean; warnings: string[] }
  | { ok: false; reduceOnly: boolean; reasons: string[]; warnings: string[] };

/** Check an outcome order against the venue rules and spec 2.5 before signing. Never throws on bad input. */
export function validateOrderIntent(o: OrderIntent): IntentResult {
  const reasons: string[] = [];
  const warnings: string[] = [];
  const label = (o.sideLabels ?? ['Yes', 'No'])[o.side] ?? (o.side === 0 ? 'Yes' : 'No');
  const reduceOnly = o.reduceOnly ?? !o.isBuy;

  if (o.side !== 0 && o.side !== 1) reasons.push(t('rules.side_invalid'));

  const szCheck = validateSize(o.sz, OUTCOME_RULES);
  if (!szCheck.ok) reasons.push(szCheck.reason);
  let pxOk = false;
  if (o.px !== undefined) {
    const pxCheck = validatePrice(o.px, OUTCOME_RULES);
    if (!pxCheck.ok) reasons.push(pxCheck.reason);
    pxOk = pxCheck.ok;
  }

  if (o.isBuy && reduceOnly) reasons.push(t('rules.buy_reduce_only'));

  if (szCheck.ok) {
    const sz = parseDec(o.sz) as Dec;
    if (!o.isBuy) {
      const held = sellable(o.side === 0 ? o.holdings.holdYes : o.holdings.holdNo);
      if (cmp(held, ZERO) === 0) {
        reasons.push(t('rules.no_shares', { side: label }));
      } else if (cmp(sz, held) > 0) {
        reasons.push(t('rules.sell_at_most', { held: localizeNumber(decToString(held)), side: label, sz: localizeNumber(decToString(sz)) }));
      }
    } else if (pxOk && o.px !== undefined) {
      const cost = mul(parseDec(o.px) as Dec, sz);
      if (o.usdcAvailable !== undefined) {
        const avail = parseDec(o.usdcAvailable);
        const a = avail && cmp(avail, ZERO) > 0 ? avail : ZERO;
        if (cmp(cost, a) > 0) {
          reasons.push(
            t('rules.not_enough_usdc', {
              cost: localizeNumber(decToString(roundDec(cost, 2, 'up'))),
              avail: localizeNumber(decToString(roundDec(a, 2, 'down'))),
            }),
          );
        }
      }
    }
    // Min order value (#749): blocks, except a sell of the whole holding (Hyperliquid accepts those).
    if (pxOk && o.px !== undefined && o.checkMinValue !== false) {
      const held = o.side === 0 ? o.holdings.holdYes : o.holdings.holdNo;
      if (o.isBuy || !sellsWholeHolding(decToString(sz), held)) {
        const e = minOrderValueError(o.px, decToString(sz), o.minOrderValueUsd);
        if (e) reasons.push(e);
      }
    }
  }

  return reasons.length === 0 ? { ok: true, reduceOnly, warnings } : { ok: false, reduceOnly, reasons, warnings };
}

// ---------------------------------------------------------------------------------------------------------------
// Wire strings: round + validate, then format through the SDK-equivalent float_to_wire (signing.ts).

/** Rounded, validated price ready for an order wire. Throws with the validation reason if invalid. */
export function wirePrice(px: Num, ctx: MarketRules, mode: RoundMode = 'nearest'): string {
  const r = roundPrice(px, ctx, mode);
  const v = validatePrice(r, ctx);
  if (!v.ok) throw new Error(v.reason);
  return floatToWire(Number(r));
}

/** Rounded (down by default), validated size ready for an order wire. Throws with the validation reason if invalid. */
export function wireSize(sz: Num, ctx: MarketRules, mode: RoundMode = 'down'): string {
  const r = roundSize(sz, ctx, mode);
  const v = validateSize(r, ctx);
  if (!v.ok) throw new Error(v.reason);
  return floatToWire(Number(r));
}
